import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { assetUrl, cn } from '@Shared/Helpers/Utilities';

// Global cache for SVG content
const svgCache = new Map();
// Track in-flight requests to prevent duplicate fetches
const pendingRequests = new Map();

// Dynamic SVG Icon component that fetches and renders SVGs from a path with caching and request deduplication
const BaseSvgImage = ({ className = null, testid, icon, path, ...rest }) => {
    const [svgContent, setSvgContent] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadSvg = async () => {
            try {
                setIsLoading(true);

                // Check if the SVG is already in cache
                if (svgCache.has(icon)) {
                    if (isMounted) {
                        setSvgContent(svgCache.get(icon));
                        setError(null);
                        setIsLoading(false);
                    }
                    return;
                }

                // Check if there's already a fetch in progress for this icon
                if (pendingRequests.has(icon)) {
                    // Wait for the existing request to complete
                    const svgText = await pendingRequests.get(icon);
                    if (isMounted) {
                        setSvgContent(svgText);
                        setError(null);
                        setIsLoading(false);
                    }
                    return;
                }

                // Create a new fetch request and store its promise
                const fetchPromise = (async () => {
                    // Construct the path to the SVG file
                    const svgPath = assetUrl(`${path}/${icon}.svg`);

                    // Fetch the SVG file
                    const response = await fetch(svgPath);

                    if (!response.ok) {
                        throw new Error(`Failed to load SVG: ${response.statusText}`);
                    }

                    const svgText = await response.text();

                    // Store in cache
                    svgCache.set(icon, svgText);

                    // Remove from pending requests
                    pendingRequests.delete(icon);

                    return svgText;
                })();

                // Add to pending requests
                pendingRequests.set(icon, fetchPromise);

                // Await the result
                const svgText = await fetchPromise;

                if (isMounted) {
                    setSvgContent(svgText);
                    setError(null);
                    setIsLoading(false);
                }
            } catch (err) {
                console.error('Error loading SVG:', err);
                // Clean up the pending request on error
                pendingRequests.delete(icon);

                if (isMounted) {
                    setError(`Could not load icon: ${icon}`);
                    setIsLoading(false);
                }
            }
        };

        if (icon) {
            loadSvg();
        }

        return () => {
            isMounted = false;
        };
    }, [icon]);

    // Parse the SVG content to create a component
    const createMarkup = () => {
        return { __html: svgContent };
    };

    if (isLoading) {
        return <div className={cn([className])}></div>;
    }

    if (error) {
        return <div className={cn([className])}>{error}</div>;
    }

    return (
        <span
            data-testid={testid}
            className={cn([className])}
            {...rest}
            dangerouslySetInnerHTML={createMarkup()}
        />
    );
};

BaseSvgImage.propTypes = {
    testid: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    className: PropTypes.string,
};

export default BaseSvgImage;
